import React, { useEffect, useMemo, useState } from 'react'
import {
	Avatar,
	Box,
	Checkbox,
	Dialog,
	FormControlLabel,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import { type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { GRID_LOCALE_TEXT } from '../../../../../utils/tableLocaleText'
import type {
	TimesheetMultipleDaysDayRow,
	TimesheetMultipleDaysDriverRow,
	TimesheetMultipleDaysModalData,
} from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const checkboxSx = {
	color: '#8695AF',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
	'&.MuiCheckbox-indeterminate': {
		color: '#00A5FF',
	},
}

const checkboxLabelSx = {
	margin: 0,
	'.MuiTypography-root': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '13px',
		fontWeight: 500,
		lineHeight: 1.3,
		color: '#092056',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(0, 165, 255, 0.3)',
	borderRadius: '6px',
	height: '24px',
	'& .MuiToggleButton-root': {
		border: 'none',
		padding: '2px',
		width: '24px',
		height: '24px',
		color: '#00A5FF',
		'&.Mui-selected': {
			backgroundColor: 'rgba(0, 165, 255, 0.15)',
			color: '#00A5FF',
		},
		'&:hover': {
			backgroundColor: 'rgba(0, 165, 255, 0.08)',
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.06)',
	},
	'& .MuiCheckbox-root': {
		color: '#8695AF',
		'&.Mui-checked': {
			color: '#00A5FF',
		},
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultStartDateValue = dayjs('02/05/2024', 'DD/MM/YYYY')
const defaultEndDateValue = dayjs('08/05/2024', 'DD/MM/YYYY')

const parseDateTimeValue = (value: string | undefined, fallback: Dayjs) => {
	if (!value) {
		return fallback
	}
	const withTime = dayjs(value, 'DD/MM/YYYY HH:mm')
	if (withTime.isValid()) {
		return withTime
	}
	const dateOnly = dayjs(value, 'DD/MM/YYYY')
	return dateOnly.isValid() ? dateOnly : fallback
}

const defaultDrivers: TimesheetMultipleDaysDriverRow[] = [
	{
		id: 'md-1',
		motorista: 'Beatriz Silva',
		nr: '0001',
		nrMec: '00001',
		idMot: '00001',
		email: 'beatriz@email.com',
		flg: 'seg',
		cpl: 'ter',
	},
	{
		id: 'md-2',
		motorista: 'Carlos Pereira',
		nr: '0002',
		nrMec: '00002',
		idMot: '00002',
		email: 'carlos@email.com',
		flg: 'qui',
		cpl: 'sex',
	},
	{
		id: 'md-3',
		motorista: 'Diana Costa',
		nr: '0003',
		nrMec: '00003',
		idMot: '00003',
		email: 'diana@email.com',
		flg: 'ter',
		cpl: 'qua',
	},
	{
		id: 'md-4',
		motorista: 'Eduardo Lima',
		nr: '0004',
		nrMec: '00004',
		idMot: '00004',
		email: 'eduardo@email.com',
		flg: 'seg',
		cpl: 'ter',
	},
	{
		id: 'md-5',
		motorista: 'Fernanda Rocha',
		nr: '0005',
		nrMec: '00005',
		idMot: '00005',
		email: 'fernanda@email.com',
		flg: 'sex',
		cpl: 'sáb',
	},
	{
		id: 'md-6',
		motorista: 'Gustavo Almeida',
		nr: '0006',
		nrMec: '00006',
		idMot: '00006',
		email: 'gustavo@email.com',
		flg: 'dom',
		cpl: 'seg',
	},
	{
		id: 'md-7',
		motorista: 'Helena Martins',
		nr: '0007',
		nrMec: '00007',
		idMot: '00007',
		email: 'helena@email.com',
		flg: 'sáb',
		cpl: 'dom',
	},
	{
		id: 'md-8',
		motorista: 'Carlos Silva',
		nr: '0008',
		nrMec: '00008',
		idMot: '00008',
		email: 'carlos@email.com',
		flg: 'seg',
		cpl: 'ter',
	},
	{
		id: 'md-9',
		motorista: 'Maria Santos',
		nr: '0009',
		nrMec: '00009',
		idMot: '00009',
		email: 'maria@email.com',
		flg: 'qua',
		cpl: 'qui',
	},
]

const defaultDays: TimesheetMultipleDaysDayRow[] = [
	{ id: 'day-1', dia: 'seg 02/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-2', dia: 'ter 03/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-3', dia: 'qua 04/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-4', dia: 'qui 05/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-5', dia: 'sex 06/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-6', dia: 'sáb 07/05/2024', parado: 'Sim', hoje: '' },
	{ id: 'day-7', dia: 'dom 08/05/2024', parado: 'Sim', hoje: '' },
]

const driverColumn: GridColDef<TimesheetMultipleDaysDriverRow> = {
	field: 'motorista',
	headerName: 'Motorista',
	width: 200,
	renderCell: params => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
			<Avatar
				src={params.row.avatarUrl}
				sx={{
					borderRadius: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: 'lightgrey 50% / cover no-repeat',
					width: 32,
					height: 32,
					fontSize: '20px',
					fontWeight: 400,
					lineHeight: 1,
				}}
			>
				{params.value?.[0] ?? 'M'}
			</Avatar>
			<Typography sx={{ fontSize: '12px', color: '#092056' }}>{params.value}</Typography>
		</Box>
	),
}

const CustomModalTimesheetMultipleDays: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as TimesheetMultipleDaysModalData | undefined
	const [handlePrint, handleSend] = modalHandlers ?? []

	const driverRows = useMemo(() => (data?.drivers?.length ? data.drivers : defaultDrivers), [data])
	const dayRows = useMemo(() => (data?.days?.length ? data.days : defaultDays), [data])

	const [startDate, setStartDate] = useState<Dayjs | null>(parseDateTimeValue(data?.startDate, defaultStartDateValue))
	const [endDate, setEndDate] = useState<Dayjs | null>(parseDateTimeValue(data?.endDate, defaultEndDateValue))
	const [isStartPickerOpen, setIsStartPickerOpen] = useState(false)
	const [isEndPickerOpen, setIsEndPickerOpen] = useState(false)
	const [selectedDriver, setSelectedDriver] = useState(data?.motorista ?? 'Todos')
	const [includeItineraries, setIncludeItineraries] = useState(data?.includeItineraries ?? true)
	const [driversDensity, setDriversDensity] = useState<DensityMode>('compact')
	const [daysDensity, setDaysDensity] = useState<DensityMode>('compact')

	useEffect(() => {
		if (!data) {
			return
		}
		setStartDate(parseDateTimeValue(data.startDate, defaultStartDateValue))
		setEndDate(parseDateTimeValue(data.endDate, defaultEndDateValue))
		setSelectedDriver(data.motorista ?? 'Todos')
		setIncludeItineraries(data.includeItineraries ?? true)
	}, [data])

	const driverOptions = useMemo(() => {
		const base = data?.motoristas?.length ? data.motoristas : driverRows.map(row => row.motorista)
		const unique = Array.from(new Set(base))
		return ['Todos', ...unique]
	}, [data, driverRows])

	const filteredDrivers = useMemo(() => {
		if (selectedDriver === 'Todos') {
			return driverRows
		}
		return driverRows.filter(row => row.motorista === selectedDriver)
	}, [driverRows, selectedDriver])

	const initialDriverSelection = useMemo(() => (filteredDrivers[0] ? [filteredDrivers[0].id] : []), [filteredDrivers])
	const [driverSelection, setDriverSelection] = useState<GridRowSelectionModel>(initialDriverSelection)

	useEffect(() => {
		setDriverSelection(initialDriverSelection)
	}, [initialDriverSelection])

	const initialDaySelection = useMemo(() => dayRows.map(row => row.id), [dayRows])
	const [daySelection, setDaySelection] = useState<GridRowSelectionModel>(initialDaySelection)

	useEffect(() => {
		setDaySelection(initialDaySelection)
	}, [initialDaySelection])

	const driverColumns = useMemo<GridColDef<TimesheetMultipleDaysDriverRow>[]>(
		() => [
			driverColumn,
			{ field: 'nr', headerName: 'Nr', width: 63 },
			{ field: 'nrMec', headerName: 'Nr Mec', width: 63 },
			{ field: 'idMot', headerName: 'ID Mot', width: 63 },
			{ field: 'email', headerName: 'Email', width: 170 },
			{ field: 'flg', headerName: 'Flg', width: 54 },
			{ field: 'cpl', headerName: 'Cpl', width: 54 },
		],
		[],
	)

	const dayColumns = useMemo<GridColDef<TimesheetMultipleDaysDayRow>[]>(
		() => [
			{ field: 'dia', headerName: 'Dia', width: 150 },
			{ field: 'parado', headerName: 'Parado', width: 80 },
			{ field: 'hoje', headerName: 'Hoje', width: 70 },
		],
		[],
	)

	const handleClearFilters = () => {
		setStartDate(parseDateTimeValue(data?.startDate, defaultStartDateValue))
		setEndDate(parseDateTimeValue(data?.endDate, defaultEndDateValue))
		setSelectedDriver('Todos')
		setIncludeItineraries(true)
	}

	const handlePrintAction = () => {
		if (typeof handlePrint === 'function') {
			handlePrint({ drivers: filteredDrivers, days: dayRows })
			return
		}
		console.log('Imprimir folhas de ponto vários dias')
	}

	const handleSendAction = () => {
		if (typeof handleSend === 'function') {
			handleSend({ drivers: filteredDrivers, days: dayRows })
			return
		}
		console.log('Enviar folhas de ponto vários dias')
	}

	if (!data) {
		return null
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					height: '582px',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box
				sx={{
					fontFamily: 'Poppins, sans-serif',
					color: '#092056',
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box sx={{ padding: '16px 24px 8px' }}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							lineHeight: 1.75,
							letterSpacing: '0.15px',
							color: '#092056',
						}}
					>
						Folhas de Ponto de vários dias
					</Typography>
				</Box>

				<Box
					sx={{
						padding: '0 24px 12px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
						flexWrap: 'wrap',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '12px',
							flexWrap: 'wrap',
						}}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								label='Data Inicial'
								value={startDate}
								onChange={newValue => setStartDate(newValue)}
								open={isStartPickerOpen}
								onOpen={() => setIsStartPickerOpen(true)}
								onClose={() => setIsStartPickerOpen(false)}
								format='DD/MM/YYYY HH:mm'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { width: '170px' }],
										onClick: () => setIsStartPickerOpen(true),
										onFocus: () => setIsStartPickerOpen(true),
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
								disableOpenPicker
								ampm={false}
							/>
						</LocalizationProvider>
						<Typography
							sx={{
								fontSize: '18px',
								fontWeight: 600,
								color: '#8695AF',
								lineHeight: 1,
							}}
						>
							–
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								label='Data Final'
								value={endDate}
								onChange={newValue => setEndDate(newValue)}
								open={isEndPickerOpen}
								onOpen={() => setIsEndPickerOpen(true)}
								onClose={() => setIsEndPickerOpen(false)}
								format='DD/MM/YYYY HH:mm'
								slotProps={{
									textField: {
										size: 'small',
										sx: [inputSx, calendarInputSx, { width: '170px' }],
										onClick: () => setIsEndPickerOpen(true),
										onFocus: () => setIsEndPickerOpen(true),
									},
									...calendarPopperSlotProps,
								}}
								slots={{ openPickerButton: EmptyButton }}
								disableOpenPicker
								ampm={false}
							/>
						</LocalizationProvider>
						<TextField
							select
							label='Motorista'
							value={selectedDriver}
							onChange={event => setSelectedDriver(event.target.value)}
							size='small'
							sx={{ ...inputSx, width: '180px' }}
							SelectProps={{
								IconComponent: KeyboardArrowDownIcon,
							}}
						>
							{driverOptions.map(option => (
								<MenuItem
									key={option}
									value={option}
								>
									{option}
								</MenuItem>
							))}
						</TextField>
						<FormControlLabel
							control={
								<Checkbox
									size='small'
									checked={includeItineraries}
									onChange={event => setIncludeItineraries(event.target.checked)}
									sx={checkboxSx}
								/>
							}
							label='Incluir itinerários, se existirem'
							sx={checkboxLabelSx}
						/>
					</Box>

					<CustomButton
						type='button'
						variant='text'
						size='small'
						onClick={handleClearFilters}
						sx={{
							padding: 0,
							minWidth: 'auto',
							fontFamily: 'Poppins, sans-serif',
							fontSize: '12px',
							fontWeight: 600,
							color: '#00A5FF',
							textTransform: 'none',
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
					>
						Limpar Filtros
					</CustomButton>
				</Box>

				<Box
					sx={{
						padding: '0 24px 12px',
						display: 'grid',
						gridTemplateColumns: 'minmax(0, 1fr) 280px',
						gap: '24px',
						flex: 1,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: '8px',
							}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
								<Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Motoristas</Typography>
								<Typography sx={{ fontSize: '12px', color: '#8695AF' }}>
									{`${driverSelection.length} selecionado(s)`}
								</Typography>
							</Box>
							<ToggleButtonGroup
								exclusive
								value={driversDensity}
								onChange={(_, value) => value && setDriversDensity(value)}
								sx={toggleGroupSx}
							>
								<ToggleButton
									value='compact'
									aria-label='Compacto'
								>
									<TableRowsIcon sx={{ fontSize: '16px' }} />
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='Espaçado'
								>
									<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
						<Box sx={{ height: 322 }}>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={filteredDrivers}
								columns={driverColumns}
								checkboxSelection
								disableRowSelectionOnClick
								density={driversDensity}
								rowSelectionModel={driverSelection}
								onRowSelectionModelChange={model => setDriverSelection(model)}
								rowHeight={driversDensity === 'compact' ? 32 : 40}
								columnHeaderHeight={54}
								pagination
								pageSizeOptions={[5, 10]}
								initialState={{
									pagination: { paginationModel: { pageSize: 10, page: 0 } },
								}}
								hideFooterSelectedRowCount
								localeText={GRID_LOCALE_TEXT}
								sx={{ ...tableSx, ...dataGridDensityTransitionSx }}
							/>
						</Box>
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								marginBottom: '8px',
							}}
						>
							<Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Dias</Typography>
							<ToggleButtonGroup
								exclusive
								value={daysDensity}
								onChange={(_, value) => value && setDaysDensity(value)}
								sx={toggleGroupSx}
							>
								<ToggleButton
									value='compact'
									aria-label='Compacto'
								>
									<TableRowsIcon sx={{ fontSize: '16px' }} />
								</ToggleButton>
								<ToggleButton
									value='standard'
									aria-label='Espaçado'
								>
									<ViewHeadlineIcon sx={{ fontSize: '16px' }} />
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
						<Box sx={{ height: 322 }}>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={dayRows}
								columns={dayColumns}
								checkboxSelection
								disableRowSelectionOnClick
								density={daysDensity}
								rowSelectionModel={daySelection}
								onRowSelectionModelChange={model => setDaySelection(model)}
								rowHeight={daysDensity === 'compact' ? 32 : 40}
								columnHeaderHeight={54}
								hideFooter
								sx={{ ...tableSx, ...dataGridDensityTransitionSx }}
							/>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '0 24px 16px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '8px',
					}}
				>
					<CustomButton
						variant='text'
						size='medium'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						size='medium'
						onClick={handlePrintAction}
						sx={popupSecondaryButtonSx}
					>
						Imprimir
					</CustomButton>
					<CustomButton
						variant='contained'
						size='medium'
						onClick={handleSendAction}
						sx={popupActionButtonSx}
					>
						Enviar
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalTimesheetMultipleDays
